import type { LevelModel } from 'shared/domain/level/types/model';
import type { LabelledEntity } from 'shared/types/commonView';
import { findById } from 'shared/utils/id/findById';
import type { DocumentationModel } from './documentationModel';
import { hasDocumentation } from './findDocumentationOnLevels';

const SEPARATOR = ' · ';
const NO_LABEL = '—';

/** A level as stored (`deleted` a Dexie 0/1) or as a model (`deleted` a boolean). */
type MaybeDeletedLevel = { deleted?: boolean | number };
type MapLevel = Pick<LevelModel, 'site' | 'label'> & MaybeDeletedLevel;

function isActiveLevel(level: MaybeDeletedLevel): boolean {
  return !level.deleted;
}

/** Whether the documentation is the map of some sublocation (level). */
export function isLevelMapDocumentation(documentation: Pick<DocumentationModel, 'levels'>): boolean {
  return documentation.levels.some(isActiveLevel);
}

/** A sublocation as the user is told it, e.g. `Building A · Ground floor`. */
export function formatSublocation(siteLabel: string | undefined, levelLabel: string | undefined): string {
  return `${siteLabel || NO_LABEL}${SEPARATOR}${levelLabel || NO_LABEL}`;
}

/** The levels as sublocations with their sites' labels, joined with commas. */
function formatSublocations(levels: MapLevel[], sites: LabelledEntity[]): string {
  return levels
    .map((level) => formatSublocation(findById(sites, level.site)?.label, level.label))
    .join(', ');
}

/**
 * The sublocations the documentation is the map of, as the user is told
 * them, e.g. `Building A · Ground floor` (PT-7117 mockup); several are joined
 * with commas.
 */
export function formatMapLocation(documentation: Pick<DocumentationModel, 'sites' | 'levels'>): string {
  return formatSublocations(documentation.levels.filter(isActiveLevel), documentation.sites);
}

/** The sublocations whose map the documentation is, read from the levels (e.g. in a preview of the file). */
export function formatMapLocationOnLevels(
  levels: (MapLevel & Pick<LevelModel, 'map'>)[],
  sites: LabelledEntity[],
  documentationId: string | undefined
): string {
  return formatSublocations(
    levels.filter((level) => isActiveLevel(level) && hasDocumentation(level, documentationId)),
    sites
  );
}
